// Buscas da tela da Gestão Comercial (#6496). Cada uma é pequena e independente:
// a tela mostra o que chegar primeiro.
import { useEffect, useState } from 'react';
import { supabase } from '../../src/lib/supabase';
import { isPendenciaDocumental } from '../../src/lib/analiseCadastralUtils';
import type { AusenciaTrabalho, HorarioTrabalho, Negociacao } from '../../types';
import { tempoUtilDecorrido } from '../../src/lib/escalaDisponibilidade';
import { FUNCOES_COM_RODIZIO } from '../../src/lib/departamentos';
import { STATUS_COM_PENDENCIA, STATUS_NA_FILA } from '../../src/lib/validacaoDados';
import {
  agruparPerdas, agruparReprovacoes, diasDesde, estimativasPorFaixa,
  type Agrupamento, type EstimativaFaixa, type FaixaConta,
} from '../../src/lib/visaoGeral/comercial';

const PAGINA = 1000;
const ENCERRADOS = '(perdido,termo_reprovado,termo_aprovado,ganho)';
/** Funções do rodízio de leads e do SLA de primeiro contato (mesmas do Analytics Comercial). */
const FUNCOES_SLA = [...FUNCOES_COM_RODIZIO];

export const DIAS_SIMULACAO_PARADA = 7;
export const DIAS_TERMO_SEM_ASSINATURA = 3;

async function paginar<T>(montar: () => any): Promise<T[]> {
  const todas: T[] = [];
  for (let p = 0; ; p++) {
    const { data, error } = await montar().range(p * PAGINA, p * PAGINA + PAGINA - 1);
    if (error) throw error;
    todas.push(...((data || []) as T[]));
    if (!data || data.length < PAGINA) break;
  }
  return todas;
}

// ── Hoje ──

export interface LeadEsperando {
  negociacaoId: number;
  cliente: string | null;
  origem: string | null;
  atribuidoEm: string;
  /** Tempo útil (expediente da pessoa) desde a atribuição; null quando é a visão do time. */
  utilMs: number | null;
}

export interface HojeComercial {
  leads: { total: number; mais24h: number; maisAntigo: string | null; lista: LeadEsperando[] } | null;
  simulacoesParadas: number | null;
  /** Termos enviados que esperam a assinatura (todos) e, deles, os parados há 3+ dias. */
  termosEnviados: number | null;
  termosParados: number | null;
  /**
   * Já analisados pela validação de dados e esperando o cliente (documento, garantia ou
   * confirmação). `documento` = quantos pedem documento (selo Pendência documental do Kanban).
   */
  comPendencia: { qtd: number; kwh: number; documento: number } | null;
  /** #6531: o que está na fila da Validação de Dados (sem as pendências, que ficam à parte). */
  emValidacao: { qtd: number; kwh: number } | null;
  erro: string | null;
}

/**
 * O que pede ação agora: leads sem primeiro contato, propostas e termos parados,
 * documento pendente. Sem `responsavelId` é o time (tela da Talita: leads do
 * Closers e BDRs, o resto de todo o comercial); com ele, só o que é da pessoa.
 */
export function useHojeComercial(responsavelId?: string): HojeComercial {
  const [estado, setEstado] = useState<HojeComercial>({
    leads: null, simulacoesParadas: null, termosEnviados: null, termosParados: null, comPendencia: null, emValidacao: null, erro: null,
  });

  useEffect(() => {
    let vivo = true;
    setEstado({ leads: null, simulacoesParadas: null, termosEnviados: null, termosParados: null, comPendencia: null, emValidacao: null, erro: null });
    const agora = new Date();
    const atualizar = (parte: Partial<HojeComercial>) => { if (vivo) setEstado(e => ({ ...e, ...parte })); };
    const falhou = (e: any) => { console.error('Visão Geral — números do dia (comercial):', e); atualizar({ erro: 'Parte dos números do dia não carregou.' }); };
    const daPessoa = (q: any) => (responsavelId ? q.eq('responsavel_id', responsavelId) : q);

    // Leads atribuídos que ainda não tiveram a primeira atividade (a mesma marca do
    // SLA de atendimento do Analytics Comercial).
    (async () => {
      let ids: string[];
      if (responsavelId) ids = [responsavelId];
      else {
        const { data: pessoas, error } = await supabase.from('profiles').select('id').in('funcao_comercial', FUNCOES_SLA);
        if (error) throw error;
        ids = (pessoas || []).map((p: any) => p.id);
      }
      if (ids.length === 0) { atualizar({ leads: { total: 0, mais24h: 0, maisAntigo: null, lista: [] } }); return; }
      const [linhas, escala] = await Promise.all([
        paginar<any>(() => supabase
          .from('negociacao_atribuicoes')
          .select('negociacao_id, atribuido_em, negociacoes!inner(status, cliente_nome, origem)')
          .in('responsavel_id', ids)
          .is('primeira_atividade_em', null)
          .is('encerrado_em', null)
          .or('sla_desconsiderado.is.null,sla_desconsiderado.eq.false')
          .not('negociacoes.status', 'in', ENCERRADOS)
          .order('atribuido_em')),
        // Relógio do SLA da pessoa: conta só o expediente dela (escala em /config/escalas).
        responsavelId
          ? Promise.all([
              supabase.from('horarios_trabalho').select('*').eq('profile_id', responsavelId),
              supabase.from('ausencias_trabalho').select('*').eq('profile_id', responsavelId),
            ])
          : Promise.resolve(null),
      ]);
      const horarios = escala ? ((escala[0].data || []) as HorarioTrabalho[]) : null;
      const ausencias = escala ? ((escala[1].data || []) as AusenciaTrabalho[]) : [];
      const limite24h = agora.getTime() - 86_400_000;
      const lista: LeadEsperando[] = linhas.map(l => {
        const neg = Array.isArray(l.negociacoes) ? l.negociacoes[0] : l.negociacoes;
        return {
          negociacaoId: Number(l.negociacao_id),
          cliente: neg?.cliente_nome ?? null,
          origem: neg?.origem ?? null,
          atribuidoEm: l.atribuido_em,
          utilMs: horarios ? tempoUtilDecorrido(horarios, ausencias, new Date(l.atribuido_em), agora) : null,
        };
      });
      atualizar({
        leads: {
          total: lista.length,
          mais24h: lista.filter(l => Date.parse(l.atribuidoEm) < limite24h).length,
          maisAntigo: lista[0]?.atribuidoEm ?? null,
          lista,
        },
      });
    })().catch(falhou);

    // Simulação feita e nada mais há 7+ dias. Leads em prospecção ficam de fora: são
    // milhares de listas frias e geográficas, e esconderiam as propostas na mão do cliente.
    (async () => {
      // Data com ':' e '.' vai entre aspas dentro de or() do PostgREST.
      const corte = `"${new Date(agora.getTime() - DIAS_SIMULACAO_PARADA * 86_400_000).toISOString()}"`;
      const { count, error } = await daPessoa(supabase
        .from('negociacoes')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'simulacao_realizada')
        .or(`ultima_atividade_at.lt.${corte},and(ultima_atividade_at.is.null,updated_at.lt.${corte})`));
      if (error) throw error;
      atualizar({ simulacoesParadas: count ?? 0 });
    })().catch(falhou);

    // Termo enviado e ainda sem assinatura; parado = último envio há 3+ dias.
    (async () => {
      const linhas = await paginar<{ url_termo: { data_criacao?: string | null }[] | null; stage_changed_at: string | null }>(() => daPessoa(supabase
        .from('negociacoes')
        .select('id, url_termo, stage_changed_at')
        .eq('status', 'termo_enviado'))
        .order('id'));
      const envio = (l: (typeof linhas)[number]) => {
        const datas = (l.url_termo || []).map(t => t?.data_criacao).filter((d): d is string => !!d).sort();
        return datas[datas.length - 1] ?? l.stage_changed_at;
      };
      atualizar({
        termosEnviados: linhas.length,
        termosParados: linhas.filter(l => diasDesde(envio(l), agora) >= DIAS_TERMO_SEM_ASSINATURA).length,
      });
    })().catch(falhou);

    // Assinados que a validação já analisou e devolveu com pendência (regra em
    // src/lib/validacaoDados.ts). Ficam fora do "em validação de dados".
    (async () => {
      const linhas = await paginar<Negociacao & { consumo_medio_kwh: number | null }>(() => daPessoa(supabase
        .from('negociacoes')
        .select('id, status, consumo_medio_kwh, analise_cadastral(decisao_final, decisao_final_at)')
        .eq('status', STATUS_COM_PENDENCIA))
        .order('id'));
      atualizar({
        comPendencia: {
          qtd: linhas.length,
          kwh: linhas.reduce((t, l) => t + (Number(l.consumo_medio_kwh) || 0), 0),
          documento: linhas.filter(isPendenciaDocumental).length,
        },
      });
    })().catch(falhou);

    // Na fila da Validação de Dados: mesma regra da etapa "Em Validação de Dados" do Analytics Comercial.
    (async () => {
      const linhas = await paginar<{ consumo_medio_kwh: number | null }>(() => daPessoa(supabase
        .from('negociacoes')
        .select('id, consumo_medio_kwh')
        .eq('status', STATUS_NA_FILA))
        .order('id'));
      atualizar({ emValidacao: { qtd: linhas.length, kwh: linhas.reduce((t, l) => t + (Number(l.consumo_medio_kwh) || 0), 0) } });
    })().catch(falhou);

    return () => { vivo = false; };
  }, [responsavelId]);

  return estado;
}

// ── Motivos do mês ──

export interface MotivosDoMes {
  reprovacoes: Agrupamento | null;
  perdas: Agrupamento | null;
  estimativas: Partial<Record<FaixaConta, EstimativaFaixa>>;
  tarifa: number | null;
  erro: string | null;
}

/** Reprovações da análise e perdas do mês, por motivo. `inicio`/`fimExclusivo` em ISO. */
/**
 * Reprovações da análise cadastral decididas no período, por motivo (#6428), com o kWh
 * de cada negociação. Usada pela Gestão Comercial e pelo Pós-venda.
 */
export async function carregarReprovacoes(inicio: string, fimExclusivo: string): Promise<Agrupamento> {
  const linhas = await paginar<any>(() => supabase
    .from('analise_cadastral')
    .select('negociacao_id, motivo_reprovacao, decisao_final_at, negociacoes!analise_cadastral_negociacao_id_fkey(consumo_medio_kwh)')
    .eq('decisao_final', 'Reprovado')
    .gte('decisao_final_at', inicio)
    .lt('decisao_final_at', fimExclusivo)
    .order('decisao_final_at'));
  return agruparReprovacoes(linhas.map(l => {
    const neg = Array.isArray(l.negociacoes) ? l.negociacoes[0] : l.negociacoes;
    return { negociacao_id: Number(l.negociacao_id), motivo: l.motivo_reprovacao, kwh: neg?.consumo_medio_kwh == null ? null : Number(neg.consumo_medio_kwh), decididoEm: l.decisao_final_at };
  }));
}

export function useMotivosDoMes(inicio: string, fimExclusivo: string): MotivosDoMes {
  const [estado, setEstado] = useState<MotivosDoMes>({ reprovacoes: null, perdas: null, estimativas: {}, tarifa: null, erro: null });

  useEffect(() => {
    let vivo = true;
    setEstado({ reprovacoes: null, perdas: null, estimativas: {}, tarifa: null, erro: null });
    const falhou = (e: any) => {
      console.error('Visão Geral — motivos do mês:', e);
      if (vivo) setEstado(s => ({ ...s, erro: 'Não foi possível carregar os motivos do mês.' }));
    };

    carregarReprovacoes(inicio, fimExclusivo)
      .then(reprovacoes => { if (vivo) setEstado(s => ({ ...s, reprovacoes })); })
      .catch(falhou);

    (async () => {
      // A estimativa por faixa sai das negociações que têm faixa E consumo (todas as épocas).
      const [amostras, tarifaRes, perdidas] = await Promise.all([
        paginar<any>(() => supabase.from('negociacoes').select('valor_conta_luz, consumo_medio_kwh')
          .not('valor_conta_luz', 'is', null).gt('consumo_medio_kwh', 0).order('id')),
        supabase.from('tarifas_energia').select('valor, vigencia_inicio').lte('vigencia_inicio', fimExclusivo.slice(0, 10))
          .order('vigencia_inicio', { ascending: false }).limit(1),
        // Perdida no mês = a última mudança de status (a perda) caiu no mês.
        paginar<any>(() => supabase.from('negociacoes').select('motivo_perda, consumo_medio_kwh, valor_conta_luz')
          .eq('status', 'perdido').gte('ultima_atividade_at', inicio).lt('ultima_atividade_at', fimExclusivo).order('id')),
      ]);
      const tarifa = tarifaRes.data?.[0]?.valor == null ? null : Number(tarifaRes.data[0].valor);
      const estimativas = estimativasPorFaixa(
        amostras.map(a => ({ faixa: a.valor_conta_luz, kwh: Number(a.consumo_medio_kwh) })),
        tarifa,
      );
      const perdas = agruparPerdas(
        perdidas.map(p => ({ motivo: p.motivo_perda, kwh: p.consumo_medio_kwh == null ? null : Number(p.consumo_medio_kwh), faixa: p.valor_conta_luz })),
        estimativas,
      );
      if (vivo) setEstado(s => ({ ...s, perdas, estimativas, tarifa }));
    })().catch(falhou);

    return () => { vivo = false; };
  }, [inicio, fimExclusivo]);

  return estado;
}

// ── Metas por vendedor e tarifa ──

export interface ParametrosDoMes {
  /** Meta de kWh de cada pessoa (0 = sem meta). */
  metas: Map<string, number>;
  /** R$/kWh vigente no mês (base da comissão de parceiro). */
  tarifa: number | null;
}

/**
 * Meta de kWh de cada pessoa no mês — a mesma da comissão (Folha de Pagamento,
 * `comissao_vendedores`, repetindo a última definida) — e a tarifa do mês. A função
 * do banco entrega tudo para adm/gerente/analista e só a própria linha para os demais.
 */
export function useParametrosComissao(ids: string[], mes: number, ano: number): ParametrosDoMes | null {
  const [estado, setEstado] = useState<ParametrosDoMes | null>(null);
  const chave = [...ids].sort().join(',');

  useEffect(() => {
    let vivo = true;
    if (!chave) { setEstado({ metas: new Map(), tarifa: null }); return; }
    supabase.rpc('parametros_comissao_mes', { p_vendedores: chave.split(','), p_mes: mes, p_ano: ano }).then(({ data, error }) => {
      if (!vivo) return;
      if (error) { console.error('Parâmetros de comissão:', error); setEstado({ metas: new Map(), tarifa: null }); return; }
      const linhas = (data || []) as any[];
      const tarifa = linhas.find(r => r.tarifa_valor != null)?.tarifa_valor;
      setEstado({
        metas: new Map(linhas.map(r => [String(r.vendedor_id), Number(r.meta_kwh) || 0])),
        tarifa: tarifa == null ? null : Number(tarifa),
      });
    });
    return () => { vivo = false; };
  }, [chave, mes, ano]);

  return estado;
}

/** Só as metas (telas da Gestão Comercial e de Vendas). */
export function useMetasVendedores(ids: string[], mes: number, ano: number): Map<string, number> | null {
  return useParametrosComissao(ids, mes, ano)?.metas ?? null;
}
